import { AxiosProgressEvent, CancelTokenSource } from 'axios';

import BaseAPI from 'utils/api/base';

import { MessageActivityResponse, OpenRequest, ActivityListResponse, FileActivityResponse } from 'ui/types';

import {
  PaginationParams,
  ActivityResponse,
  APIRequest,
  QueryParams,
  TemplateSearchResponse,
  UuidableName,
} from 'utils/api/types';

import { env } from 'config';

import {
  InspectionSearchResponse,
  InspectionQuestionsSearchResponse,
  TemplateDetailsResponse,
  TemplateListResponse,
  FormsDetailsResponse,
  TemplateCreateType,
  FormUpdateData,
} from './workflows.types';

class WorkflowsAPI extends BaseAPI {
  searchTemplates = (
    params?: Partial<{
      query: string;
      limit: number;
    }>
  ): APIRequest<TemplateSearchResponse[]> => {
    const url = '/templates/search/';
    const method = 'GET';

    return this.request<TemplateSearchResponse[]>({
      url,
      method,
      params,
      baseURL: env.REACT_APP_WORKFLOWS_API_URL,
    });
  };

  loadTemplateDetails = (templateId: string): APIRequest<TemplateDetailsResponse> => {
    const url = `/templates/${templateId}/`;
    const method = 'GET';

    return this.request<TemplateDetailsResponse>({
      url,
      method,
      baseURL: env.REACT_APP_WORKFLOWS_API_URL,
    });
  };

  createTemplate = (data: { name: string; content: object }): APIRequest<TemplateListResponse> => {
    const url = '/templates/';
    const method = 'POST';

    return this.request<TemplateListResponse>({
      url,
      method,
      data,
      baseURL: env.REACT_APP_WORKFLOWS_API_URL,
    });
  };

  updateTemplate = (
    templateId: string,
    data: { name?: string; isActive?: boolean; content?: object }
  ): APIRequest<TemplateDetailsResponse> => {
    const url = `/templates/${templateId}/`;
    const method = 'PATCH';

    return this.request<TemplateDetailsResponse>({
      url,
      method,
      data,
      baseURL: env.REACT_APP_WORKFLOWS_API_URL,
    });
  };

  downloadFormPDFReport = (inspectionId: string, params: QueryParams = {}): OpenRequest => {
    const url = `/api/inspections/${inspectionId}/export/pdf/`;
    return this.open({ url, baseURL: env.REACT_APP_WORKFLOWS_API_URL, params });
  };

  loadActivitiesAnswer = (
    inspectionId: string,
    answerId: string,
    params: PaginationParams
  ): APIRequest<ActivityListResponse> => {
    const method = 'GET';
    const url = `/inspections/${inspectionId}/questions/${answerId}/activities/`;

    return this.request<ActivityListResponse>({
      url,
      method,
      params,
      baseURL: env.REACT_APP_WORKFLOWS_API_URL,
    });
  };

  searchInspectionQuestion = (
    inspectionId: string,
    params?: Partial<{ query: string }>
  ): APIRequest<InspectionQuestionsSearchResponse[]> => {
    const url = `/inspections/${inspectionId}/questions/search/`;
    const method = 'GET';

    return this.request<InspectionQuestionsSearchResponse[]>({
      url,
      method,
      params,
      baseURL: env.REACT_APP_WORKFLOWS_API_URL,
    });
  };

  createInspection = (data: TemplateCreateType): APIRequest<UuidableName> => {
    const url = '/inspections/';
    const method = 'POST';

    return this.request<UuidableName>({
      url,
      method,
      data,
      baseURL: env.REACT_APP_WORKFLOWS_API_URL,
    });
  };

  searchInspections = (
    params?: Partial<{
      query: string;
      limit: number;
    }>
  ): APIRequest<InspectionSearchResponse[]> => {
    const url = '/inspections/search/';
    const method = 'GET';

    return this.request<InspectionSearchResponse[]>({
      url,
      method,
      params,
      baseURL: env.REACT_APP_WORKFLOWS_API_URL,
    });
  };

  loadFormsDetails = (inspectionId: string): APIRequest<FormsDetailsResponse> => {
    const url = `/inspections/${inspectionId}/`;
    const method = 'GET';

    return this.request<FormsDetailsResponse>({
      url,
      method,
      baseURL: env.REACT_APP_WORKFLOWS_API_URL,
    });
  };

  deleteForm = (inspectionId: string): APIRequest<void> => {
    const method = 'DELETE';
    const url = `/inspections/${inspectionId}/`;

    return this.request<void>({
      url,
      method,
      baseURL: env.REACT_APP_WORKFLOWS_API_URL,
    });
  };

  updateForm = (formId: string, data: FormUpdateData): APIRequest<FormsDetailsResponse> => {
    const url = `/inspections/${formId}/`;
    const method = 'PATCH';

    return this.request<FormsDetailsResponse>({
      url,
      method,
      data,
      baseURL: env.REACT_APP_WORKFLOWS_API_URL,
    });
  };

  addAnswerMessage = (inspectionId: string, answerId: string, message: string): APIRequest<MessageActivityResponse> => {
    const method = 'POST';
    const url = `/inspections/${inspectionId}/questions/${answerId}/comments/`;

    return this.request<MessageActivityResponse>({
      url,
      method,
      data: { message },
      baseURL: env.REACT_APP_WORKFLOWS_API_URL,
    });
  };

  removeAnswerMessage = (inspectionId: string, answerId: string, commentId: string): APIRequest<void> => {
    const method = 'DELETE';
    const url = `/inspections/${inspectionId}/questions/${answerId}/comments/${commentId}/`;

    return this.request<void>({
      url,
      method,
      baseURL: env.REACT_APP_WORKFLOWS_API_URL,
    });
  };

  uploadInspectionFile = (
    inspectionId: string,
    answerId: string,
    file: File,
    onUploadProgress: (event: AxiosProgressEvent) => void,
    cancelTokenSource: CancelTokenSource
  ): APIRequest<ActivityResponse> => {
    const method = 'POST';
    const url = `/inspections/${inspectionId}/questions/${answerId}/activities/files/`;

    const data = new FormData();
    data.append('file', file);

    return this.request<ActivityResponse>({
      url,
      method,
      data,
      onUploadProgress,
      cancelTokenSource,
      baseURL: env.REACT_APP_WORKFLOWS_API_URL,
    });
  };

  removeInspectionFile = (inspectionId: string, answerId: string, fileId: string): APIRequest<void> => {
    const method = 'DELETE';
    const url = `/inspections/${inspectionId}/questions/${answerId}/activities/files/${fileId}/`;

    return this.request<void>({
      url,
      method,
      baseURL: env.REACT_APP_WORKFLOWS_API_URL,
    });
  };

  loadInspectionAttachments = (inspectionId: string): APIRequest<ActivityListResponse> => {
    const method = 'GET';
    const url = `/inspections/${inspectionId}/attachments/`;

    return this.request<ActivityListResponse>({
      url,
      method,
      baseURL: env.REACT_APP_WORKFLOWS_API_URL,
    });
  };

  uploadInspectionAttachment = (
    inspectionId: string,
    file: File,
    onUploadProgress: (event: AxiosProgressEvent) => void,
    cancelTokenSource: CancelTokenSource
  ): APIRequest<FileActivityResponse> => {
    const method = 'POST';
    const url = `/inspections/${inspectionId}/attachments/`;

    const data = new FormData();
    data.append('file', file);

    return this.request<FileActivityResponse>({
      url,
      method,
      data,
      onUploadProgress,
      cancelTokenSource,
      baseURL: env.REACT_APP_WORKFLOWS_API_URL,
    });
  };

  removeInspectionAttachment = (inspectionId: string, fileId: string): APIRequest<void> => {
    const method = 'DELETE';
    const url = `/inspections/${inspectionId}/attachments/${fileId}/`;

    return this.request<void>({
      url,
      method,
      baseURL: env.REACT_APP_WORKFLOWS_API_URL,
    });
  };

  duplicateTemplate = (templateId: string): APIRequest<TemplateListResponse> => {
    const method = 'POST';
    const url = `/templates/${templateId}/duplicate/`;

    return this.request<TemplateListResponse>({
      url,
      method,
      baseURL: env.REACT_APP_WORKFLOWS_API_URL,
    });
  };
}

export default WorkflowsAPI;
